/**
 * SamplePlayer — 60 seconds of a Deep Rest opening on the landing page. A
 * fixed, non-personal excerpt (public/audio/deep-rest-sample.mp3). Nobody
 * has heard the product before signing up; this is the first time they can.
 */

import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Headphones } from 'lucide-react';
import { track } from '../lib/analytics';

const SRC = '/audio/deep-rest-sample.mp3';

export default function SamplePlayer({ className = '' }) {
  const ref = useRef(null);
  const tracked = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [pct, setPct] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => () => { ref.current?.pause(); }, []);

  const toggle = async () => {
    const a = ref.current;
    if (!a) return;
    try {
      if (a.paused) {
        await a.play();
        if (!tracked.current) { tracked.current = true; track('sample_play', { where: 'hero' }); }
      } else {
        a.pause();
      }
    } catch {
      setFailed(true);
    }
  };

  if (failed) return null;

  return (
    <div className={`bg-slate-900/70 border border-indigo-900/40 rounded-xl p-4 text-left ${className}`}>
      <audio
        ref={ref}
        src={SRC}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setPct(0); }}
        onTimeUpdate={(e) => { const a = e.currentTarget; if (a.duration) setPct((a.currentTime / a.duration) * 100); }}
        onError={() => setFailed(true)}
      />
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          aria-label={playing ? 'Pause the sample' : 'Play a 60 second sample'}
          className="w-12 h-12 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white flex items-center justify-center flex-shrink-0 shadow-glow transition-colors"
        >
          {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-200">Hear 60 seconds of Deep Rest</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Every evening the Navigator turns the thing you said you would do into a guided rest, in your own words. This is only the opening.
          </p>
          <div className="mt-2 h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-[width]" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-600 mt-2 flex items-center gap-1.5"><Headphones size={11} /> Headphones make it better.</p>
    </div>
  );
}
